// The video room under the canvas: connect, publish what the join screen prepared, play everyone's audio, keep
// the person told when the connection drops, and say how it ended. The canvas views build on this.
import { DisconnectReason, Room, RoomEvent, Track } from "livekit-client";
import { h } from "./dom.js";
import { roomOptions } from "./rtc.js";

export async function openSession({ pass, joined }) {
  // The room's settings — the picture's quality and the five minutes of reconnecting — are rtc.js's, shared with the call.
  const room = new Room(roomOptions(joined));
  const audioBox = h("div", { hidden: true });
  document.body.append(audioBox);
  let bannerEl = null, unlockEl = null, endedBy = null;
  // The screen stays awake for the meeting (audit F11): a phone left alone while the sales manager talks would lock,
  // and iOS then suspends the page — her microphone stops and she drops out. Asked again whenever the page is back.
  let wakeLock = null;
  const keepAwake = async () => {
    if (document.visibilityState !== "visible" || !("wakeLock" in navigator) || (wakeLock && !wakeLock.released)) return;
    try { wakeLock = await navigator.wakeLock.request("screen"); } catch { /* refused, or a low battery */ }
  };
  document.addEventListener("visibilitychange", keepAwake);

  const banner = (text) => {
    bannerEl?.remove();
    bannerEl = text ? h("div", { class: "banner", role: "status", text }) : null;
    if (bannerEl) document.body.append(bannerEl);
  };
  const audioGate = () => {
    unlockEl?.remove();
    unlockEl = null;
    if (!room.canPlaybackAudio) {
      unlockEl = h("button", { class: "btn btn-gold audio-unlock", type: "button", text: "Tap to hear the meeting", onclick: () => room.startAudio().then(audioGate) });
      document.body.append(unlockEl);
    }
  };

  room
    .on(RoomEvent.TrackSubscribed, (track) => { if (track.kind === Track.Kind.Audio) audioBox.append(track.attach()); })
    .on(RoomEvent.TrackUnsubscribed, (track) => { if (track.kind === Track.Kind.Audio) track.detach().forEach((el) => el.remove()); })
    .on(RoomEvent.Reconnecting, () => banner("Your connection dropped — reconnecting you to the meeting… Keep this page open."))
    .on(RoomEvent.Reconnected, () => banner(""))
    .on(RoomEvent.AudioPlaybackStatusChanged, audioGate);

  const ended = new Promise((resolve) => {
    room.on(RoomEvent.Disconnected, (reason) => {
      const why = endedBy || (reason === DisconnectReason.ROOM_DELETED || reason === DisconnectReason.ROOM_CLOSED ? "ended"
        : reason === DisconnectReason.PARTICIPANT_REMOVED ? "removed"
        : reason === DisconnectReason.DUPLICATE_IDENTITY ? "elsewhere"
        : reason === DisconnectReason.CLIENT_INITIATED ? "left" : "lost");
      banner("");
      unlockEl?.remove();
      audioBox.remove();
      document.removeEventListener("visibilitychange", keepAwake);
      wakeLock?.release().catch(() => {});
      for (const pub of room.localParticipant.trackPublications.values()) pub.track?.stop();
      joined.audioTrack?.stop();
      joined.videoTrack?.stop();
      resolve(why);
    });
  });

  // listenOnly (the site camera, stage 11): nothing is subscribed until the page asks — it takes voices, never pictures.
  await room.connect(pass.url, pass.token, joined.listenOnly ? { autoSubscribe: false } : undefined);
  const lp = room.localParticipant;
  if (joined.audioTrack) {
    await lp.publishTrack(joined.audioTrack, { source: Track.Source.Microphone });
    if (!joined.micOn) await lp.setMicrophoneEnabled(false);
  }
  if (joined.videoTrack) {
    if (joined.camOn) await lp.publishTrack(joined.videoTrack, { source: Track.Source.Camera });
    else joined.videoTrack.stop();
  }
  audioGate();
  keepAwake();

  return {
    room,
    ended,
    leave() { endedBy = "left"; room.disconnect(true); },
  };
}
